/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import type Booking from '@linc-technologies/linced-common/models/bookings/booking';
import type Event from '@linc-technologies/linced-common/models/bookings/event';
import type Reservation from '@linc-technologies/linced-common/models/bookings/reservation';
import type Slot from '@linc-technologies/linced-common/models/bookings/slot';
import type Group from '@linc-technologies/linced-common/models/group';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';
import type SessionService from '@linc-technologies/linced-common/services/session';
import {
  findBy,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';

// Proto: bookings.Allocation.
export default class Allocation extends Model {
  declare [Type]: 'bookings/allocation';

  @service
  declare session: SessionService;

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // eventId is the id of the event.
  @belongsTo('bookings/event', { async: true, inverse: 'allocations' })
  declare eventId: AsyncBelongsTo<Event>;

  @alias('eventId')
  declare event: AsyncBelongsTo<Event>;

  @alias('event.id')
  declare event_id: string | null | undefined;

  // slotId is the id of the slot this allocation relates to.
  @belongsTo('bookings/slot', { async: true, inverse: 'allocations' })
  declare slotId: AsyncBelongsTo<Slot>;

  @alias('slotId')
  declare slot: AsyncBelongsTo<Slot>;

  @alias('slot.id')
  declare slot_id: string | null | undefined;

  // teacherIds is the list of teachers associated with the allocation.
  @hasMany('person', { async: true, inverse: null })
  declare teacherIds: AsyncHasMany<Person>;

  @alias('teacherIds')
  declare teachers: AsyncHasMany<Person>;

  @computed('teachers.content.[]')
  get teacher_ids() {
    return resolved(this.teachers).map((teacher) => teacher.id);
  }

  // groupIds is the list of groups associated with the allocation.
  @hasMany('group', { async: true, inverse: null })
  declare groupIds: AsyncHasMany<Group>;

  @alias('groupIds')
  declare groups: AsyncHasMany<Group>;

  @computed('groups.content.[]')
  get group_ids() {
    return resolved(this.groups).map((group) => group.id);
  }

  // status is the current status of the allocation.
  // DEPRECATED
  @attr('number', { defaultValue: 0 })
  declare status: number;

  // reservedBy is the id of the user that set the allocation status to ALLOCATIONSTATUS_RESERVED
  // DEPRECATED
  @attr('string', { defaultValue: '' })
  declare reservedBy: string;

  // reservedAt is the time the allocation status was set to ALLOCATIONSTATUS_RESERVED
  // DEPRECATED
  @attr('number')
  declare reservedAt: number | undefined;

  // reservations is the list of reservations associated with the allocation.
  @fragmentArray('bookings/reservation')
  declare reservations: FragmentArray<Reservation>;

  // maxBookings is the maximum number of bookings for the allocation.
  @attr('number', { defaultValue: 0 })
  declare maxBookings: number;

  // available is the number of available bookings for the allocation.
  @attr('number', { defaultValue: 0 })
  declare available: number;

  // booked is the number of booked bookings for the allocation.
  @attr('number', { defaultValue: 0 })
  declare booked: number;

  // reserved is the number of reserved bookings for the allocation.
  @attr('number', { defaultValue: 0 })
  declare reserved: number;

  // blocked indicates the allocation has been blocked from being booked.
  @attr('boolean', { defaultValue: false })
  declare blocked: boolean;

  // schoolId is the school the booking relates to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // campusId is the school campus the booking relates to.
  @attr('string', { defaultValue: '' })
  declare campusId: string;

  @hasMany('bookings/booking', { async: true, inverse: 'allocationId' })
  declare bookings: AsyncHasMany<Booking>;

  @computed('bookings.content.[]')
  get bookedStudentIds() {
    return resolved(this.bookings).map((booking) => booking.student_id);
  }

  @computed('reservations.@each.personId', 'session.personId')
  get reservedByUser() {
    return this.reservations.any((reservation) => {
      return reservation.get('personId') === this.session.get('personId');
    });
  }

  @computed('reservations.@each.personId', 'session.personId')
  get reservedAtByUser() {
    const reservation = findBy(
      this.reservations,
      'personId',
      this.session.get('personId'),
    );
    if (!isEmpty(reservation)) {
      return reservation!.get('createTime');
    }
  }

  @computed('reservedByUser', 'blocked', 'booked', 'reserved', 'maxBookings')
  get chipStyle() {
    if (this.blocked) {
      return 'booking-status-blocked';
    }
    if (this.reservedByUser) {
      return 'booking-status-reserved';
    }
    if (this.booked === this.maxBookings) {
      return 'booking-status-booked';
    }
    if (this.booked + this.reserved === this.maxBookings) {
      return 'booking-status-reserved';
    }
    return 'booking-status-available';
  }
}
